// @givmo/board — a committee's decisions for the board, as the backend serves them (CBG-YR6-CONFIRM;
// the backend's `board-committee-confirmation.md` §4 and §8).
//
// Two reads, both computed by the backend from the committee's own records, nothing stored but the
// officer's press and dismissal:
//   * the suggestion on a board meeting (`BoardCommitteeSuggestionsOut`): whether it is offered there,
//     whether it was put away, and each practising committee with its decisions not yet on an agenda;
//   * a committee's page (`BoardCommitteeOut.decisions`): its decisions recorded under the practice,
//     each with where it is — on a board meeting's agenda (and the board's motion under that item, as
//     recorded), or the board meeting it is suggested for.
// Read tolerantly: a malformed row is dropped, an unknown kind reads as a decision without a tally,
// and a missing list is an empty one. Board never reads a decision's words to decide anything.

import type { BoardCommitteeOut } from '@givmo/api-client';

export interface CommitteeDecision {
  /** `motion` or `written_consent`; anything else is still a decision, read without a tally. */
  kind: string;
  id: string;
  /** Its day: a motion's meeting's, a consent's execution's (a calendar date). */
  decidedOn: string | null;
  words: string;
  meetingId: string | null;
  votesFor: number | null;
  votesAgainst: number | null;
}

export interface SuggestedCommittee {
  committeeId: string;
  name: string;
  citation: string | null;
  decisions: CommitteeDecision[];
}

export interface CommitteeSuggestions {
  offered: boolean;
  dismissedAt: string | null;
  committees: SuggestedCommittee[];
}

/** A board meeting named on a committee's page: its id, title and day. */
export interface BoardMeetingRef {
  meetingId: string;
  title: string;
  on: string | null;
}

export interface CommitteeDecisionLine extends CommitteeDecision {
  onAgenda:
    | (BoardMeetingRef & {
        agendaItemId: string | null;
        boardMotion: { outcome: 'carried' | 'failed'; on: string | null } | null;
      })
    | null;
  suggestedFor: BoardMeetingRef | null;
}

type Row = Record<string, unknown>;

function isRow(value: unknown): value is Row {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

function decision(row: unknown): CommitteeDecision | null {
  if (!isRow(row)) return null;
  const id = text(row.id);
  if (id === null) return null;
  return {
    kind: text(row.kind) ?? 'motion',
    id,
    decidedOn: text(row.decided_on),
    words: text(row.words) ?? '',
    meetingId: text(row.meeting_id),
    votesFor: count(row.votes_for),
    votesAgainst: count(row.votes_against),
  };
}

function meetingRef(value: unknown): BoardMeetingRef | null {
  if (!isRow(value)) return null;
  const meetingId = text(value.meeting_id);
  if (meetingId === null) return null;
  return { meetingId, title: text(value.title) ?? '', on: text(value.on) };
}

/** The suggestion read's answer; `null` for a body that isn't one. */
export function extractCommitteeSuggestions(body: unknown): CommitteeSuggestions | null {
  if (!isRow(body)) return null;
  const committees: SuggestedCommittee[] = [];
  for (const row of Array.isArray(body.committees) ? body.committees : []) {
    if (!isRow(row)) continue;
    const committeeId = text(row.committee_id);
    const name = text(row.name);
    if (committeeId === null || name === null) continue;
    const decisions = (Array.isArray(row.decisions) ? row.decisions : [])
      .map(decision)
      .filter((entry): entry is CommitteeDecision => entry !== null);
    if (decisions.length === 0) continue;
    committees.push({ committeeId, name, citation: text(row.citation), decisions });
  }
  return {
    offered: body.offered === true,
    dismissedAt: text(body.dismissed_at),
    committees,
  };
}

/** A committee's decisions on its page, newest first as served; `[]` while its practice is off. */
export function committeeDecisionLines(committee: BoardCommitteeOut): CommitteeDecisionLine[] {
  const rows: unknown = (committee as { decisions?: unknown }).decisions;
  const lines: CommitteeDecisionLine[] = [];
  for (const row of Array.isArray(rows) ? rows : []) {
    const base = decision(row);
    if (base === null || !isRow(row)) continue;
    const agenda = isRow(row.on_agenda) ? row.on_agenda : null;
    const agendaMeeting = meetingRef(agenda);
    const motion = agenda !== null && isRow(agenda.board_motion) ? agenda.board_motion : null;
    const outcome = motion !== null ? text(motion.outcome) : null;
    lines.push({
      ...base,
      onAgenda:
        agenda !== null && agendaMeeting !== null
          ? {
              ...agendaMeeting,
              agendaItemId: text(agenda.agenda_item_id),
              boardMotion:
                motion !== null && (outcome === 'carried' || outcome === 'failed')
                  ? { outcome, on: text(motion.on) }
                  : null,
            }
          : null,
      suggestedFor: meetingRef(row.suggested_for),
    });
  }
  return lines;
}

/** Whether the committee's practice is on, as served (an older backend serves neither field). */
export function practiceOf(committee: BoardCommitteeOut): { on: boolean; citation: string | null } {
  const served = committee as {
    confirm_with_board?: unknown;
    confirm_with_board_citation?: unknown;
  };
  return {
    on: served.confirm_with_board === true,
    citation: text(served.confirm_with_board_citation),
  };
}

/** One source's block on the card: a meeting's motions, or one written consent. */
export interface DecisionBlock {
  key: string;
  kind: 'meeting' | 'consent';
  day: string | null;
  decisions: CommitteeDecision[];
}

/** The card's blocks, in the read's order (oldest first): consecutive motions of one meeting share
 *  their meeting's block; a written consent is a block of its own. */
export function decisionBlocks(decisions: readonly CommitteeDecision[]): DecisionBlock[] {
  const blocks: DecisionBlock[] = [];
  for (const entry of decisions) {
    const last = blocks[blocks.length - 1];
    if (entry.kind === 'written_consent') {
      blocks.push({
        key: `consent:${entry.id}`,
        kind: 'consent',
        day: entry.decidedOn,
        decisions: [entry],
      });
    } else if (
      last !== undefined &&
      last.kind === 'meeting' &&
      last.key === `meeting:${entry.meetingId}`
    ) {
      last.decisions.push(entry);
    } else {
      blocks.push({
        key: `meeting:${entry.meetingId}`,
        kind: 'meeting',
        day: entry.decidedOn,
        decisions: [entry],
      });
    }
  }
  return blocks;
}
